<template>
  <div class="flex flex-col w-full items-center">
    <div class="h-[40px]">
      <div v-if="error" class="w-full min-h-[40px] text-red-500 text-center">
        <div v-html="error"></div>
      </div>
      <div
        v-if="warning"
        class="w-full min-h-[40px] text-orange-500 text-center"
      >
        <div v-html="warning"></div>
      </div>
      <div
        v-if="success"
        class="w-full min-h-[40px] text-green-500 text-center"
      >
        <div v-html="success"></div>
      </div>
    </div>
    <div class="w-full md:w-1/2 px-8">
      <el-input
        v-model="rawEmail"
        @keydown.enter.prevent="handleReviewerCreate"
      >
        <template #prepend>
          <span class="font-bold w-[110px] text-right">Client's email</span>
        </template>
      </el-input>

      <el-input
        class="mt-2"
        v-model="rawPin"
        @keydown.enter.prevent="handleReviewerCreate"
      >
        <template #prepend>
          <span class="font-bold w-[110px] text-right">PIN</span>
        </template>
      </el-input>
      <el-tooltip
        v-if="!isPayConnected"
        effect="dark"
        content="Connect and enable a payment method or fill out bank or zelle information in settings to enable collecting payments"
      >
        <el-input
          class="mt-2"
          v-model="optionalPayment"
          placeholder="0.00"
          :disabled="!isPayConnected"
        >
          <template #prepend>
            <span class="font-bold w-[110px] text-right">Invoice amount $</span>
          </template>
        </el-input>
      </el-tooltip>
      <el-input
        v-else
        class="mt-2"
        v-model="optionalPayment"
        placeholder="0.00"
        :disabled="!isPayConnected"
        @keydown.enter.prevent="handleReviewerCreate"
      >
        <template #prepend>
          <span class="font-bold w-[110px] text-right">Invoice amount $</span>
        </template>
      </el-input>
      <div class="min-h-[25px]">
        <div v-if="totalWithSurcharge">
          Total with ACH surcharges:
          {{ formatCurrency(totalWithSurchargeCents) }}
        </div>
      </div>
    </div>
    <div class="flex w-full md:w-2/3 mx-auto justify-center mb-4">
      <div :class="buttonsClass">
        <PulseLoader class="mt-4" v-if="creating" />
        <el-button
          v-else
          class="mt-4"
          type="success"
          size="large"
          plain
          @click="handleReviewerCreate"
        >
          Create Link
        </el-button>
      </div>
      <div :class="buttonsClass" v-if="isBoss">
        <el-button
          class="mt-4"
          type="primary"
          size="large"
          plain
          @click="createDummy"
        >
          Create Dummy
        </el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import PulseLoader from "vue-spinner/src/PulseLoader.vue";
import { useReviewers, useUser } from "@/stores";
import { formatCurrency } from "@/utils";

const emit = defineEmits(["pin-grab"]);

const reviewersStore = useReviewers();
const userStore = useUser();
const rawEmail = ref("");
const rawPin = ref("");
const optionalPayment = ref("");
const error = ref("");
const warning = ref("");
const creating = ref(false);
const success = ref("");
const totalWithSurcharge = ref("");

const isBoss = computed(
  () => userStore.user.email === import.meta.env.VITE_APP_EXTENDABLE_USER
);

const totalWithSurchargeCents = computed(() => {
  if (!totalWithSurcharge.value) {
    return 0;
  }
  return convertToAmountInCents(totalWithSurcharge.value);
});

const isPayConnected = computed(() => {
  const settings = userStore.settings;
  if (Object.keys(settings).length === 0) {
    userStore.getSettings();
  }

  return (
    (settings?.squareConnected && settings?.acceptSquarePayments) ||
    (settings?.stripeConnected && settings?.acceptStripePayments) ||
    (settings?.qbConnected && settings?.acceptQbPayments) ||
    (settings?.bankAccountNumber && settings?.bankRoutingNumber) ||
    settings?.zelleEmail
  );
});

function convertToAmountInCents(input) {
  input = String(input);

  if (!input.includes(".")) {
    return parseInt(input) * 100;
  }
  let [dollars, cents = ""] = input.split(".");
  if (cents.length === 0) {
    cents = "00";
  } else if (cents.length === 1) {
    cents = cents + "0";
  }

  return parseInt(dollars + cents);
}

const handleReviewerCreate = async () => {
  creating.value = true;
  error.value = "";

  const existingReviewer = reviewersStore.reviewers.find(
    (reviewer) =>
      reviewer.email === rawEmail.value &&
      reviewer.pin === rawPin.value &&
      reviewer?.invoice?.amountDue ===
        convertToAmountInCents(optionalPayment.value)
  );

  if (existingReviewer) {
    error.value = `Reviewer with email ${rawEmail.value} and amount $ ${optionalPayment.value} already exists, see matching results below`;
    await reviewersStore.getReviewerByPinAndEmail(rawPin.value, rawEmail.value);
    creating.value = false;
    return;
  }

  const AmountInCents = convertToAmountInCents(optionalPayment.value);
  const result = await reviewersStore.createReviewer(
    rawEmail.value,
    rawPin.value,
    AmountInCents
  );
  if (result.error && result.error != "") {
    success.value = "";
    error.value = result.message;
    creating.value = false;
    return;
  }

  rawEmail.value = "";
  rawPin.value = "";
  optionalPayment.value = "";
  creating.value = false;
  error.value = "";

  success.value = result.message;
  emit("pin-grab");

  setTimeout(() => {
    success.value = "";
  }, 3000);
};

const createDummy = async () => {
  await reviewersStore.createDummy();
  emit("pin-grab");
};

const paymentRegex = /^\d+(\.\d{1,2})?$/;
const buttonsClass = "w-1/2 md:px-8 flex justify-center";

watch(optionalPayment, (newValue) => {
  const numericalValue = parseFloat(newValue);
  if (paymentRegex.test(newValue) || newValue === "") {
    error.value = "";
    const surcharge = 0.03;
    totalWithSurcharge.value = parseFloat(
      (numericalValue * (1 + surcharge)).toFixed(2)
    );
    if (totalWithSurcharge.value > 999999.99) {
      warning.value =
        "Invoice amounts over $999,999.99 after including ACH surcharges will only offer payment by phone, Quickpay, or bank wire";
    } else {
      warning.value = "";
    }
  } else {
    error.value = "Please enter a valid payment amount or leave blank";
  }
});

watch(
  () => userStore.settings,
  (newVal) => {
    if (!newVal) {
      setTimeout(() => {
        userStore.getSettings();
      }, 60000);
    }
  },
  { immediate: true }
);
</script>
